import { Form, Formik } from "formik";
import { atom, useAtom } from "jotai";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { formDataAtom } from "../App";
import axios from "axios";
import { useRef, useState } from "react";
import useFocusOnLoad from "../hooks/useFocusOnLoad";
import {
  WITH_APP,
  WITH_TRAVELCARD,
  WITH_BLIP,
  WITH_TICKET,
  NO_TICKET,
} from "./TicketType";
import { ADULT, NOT_ADULT } from "./AgeCheck";
import {
  APPEAL_FOR_MYSELF,
  FIDUCIARY,
  HAVE_FIDUCIARY,
  PARENT,
} from "./AppealForWho";
import { FormModel } from "../types/FormTypes";

import {
  Box,
  Spinner,
  Button,
  handleSubmitClick,
  ErrorList,
  Progressbar,
  CheckboxForm,
  Icon,
  Heading,
} from "@ogt/ui";

import { URLS } from "src/config/urls";

interface SummaryModel {
  gdprAccept: boolean;
}
interface Issue {
  id: string;
}

export const issueAtom = atom<Issue>({
  id: "",
});

export default function Summary() {
  const [formData, setFormData] = useAtom(formDataAtom);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const h2Ref = useFocusOnLoad<HTMLHeadingElement>();
  const navigate = useNavigate();
  const progress = 100;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setIssueId] = useAtom(issueAtom);
  const formRef = useRef<HTMLFormElement>(null);

  function GetEditButtonForNavigation(path: string, text?: string) {
    return (
      <Button
        type="button"
        variant="ghost"
        showIcon
        iconName="square-pen"
        iconPosition="left"
        onClick={() => navigate(path)}
      >
        {text ?? "Gå tillbaka och ändra"}
      </Button>
    );
  }

  return (
    <div>
      <Formik<SummaryModel>
        onSubmit={(values) => {

          if (isFormSubmitted) {
            return;
          }

          setIsFormSubmitted(true);

          const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

          axios
            .post(
              `${apiBaseUrl?.replace(/\/$/, "") ?? ""}/crm/appealOfSurcharge `,
              formData,
              {
                headers: {
                  "referrer-policy": "strict-origin",
                },
              }
            )
            .then((result) => {
              setIssueId({ id: result.data });
              setFormData({
                surchargenumber: "",
                ticketType: "",
                travelcardnumber: "",
                ticketNumber: "",
                description: "",
                firstname: "",
                lastname: "",
                phone: "",
                email: "",
                agecheck: "",
                appealforwho: "",
                reporterEmail: "",
              });

              const emailToShow =
                formData.appealforwho === APPEAL_FOR_MYSELF
                  ? formData.email
                  : formData.reporterEmail;

              navigate("/tack", { state: { email: emailToShow } });
            })
            .catch((error) => {
              setIsFormSubmitted(false);
              navigate("../ett-fel-intraffade");
            });
        }}
        initialValues={{
          gdprAccept: false,
        }}
        validateOnChange={false}
        validateOnBlur={false}
        validationSchema={Yup.object().shape({
          gdprAccept: Yup.bool().test(
            "checkbox-selected",
            "Du måste godkänna hanteringen av dina personuppgifter.",
            (value) => {
              return value === true;
            }
          ),
          ...formValidation(formData),
        })}
        enableReinitialize
      >
        {({ errors, values }) => (
          <>
            <div className="flex justify-end items-center">
              <div className="w-full max-w-xs mb-4">
                <Progressbar percentage={progress} />
              </div>
            </div>

            <Form>
              <Box fullwidth>
                {isFormSubmitted && <Spinner label="Skickar ..." />}
                <div
                  className={`inner-summary ${isFormSubmitted === true ? "hide" : ""
                    }`}
                >
                  <Heading progress={progress} headingRef={h2Ref} focusable>
                    Granska din överklagan
                  </Heading>

                  {Object.keys(errors).length > 0 && (
                    <ErrorList errors={errors} />
                  )}

                  <p>
                    Gå igenom alla uppgifter innan du skickar in din överklagan
                    för tilläggsavgift.
                  </p>
                  <p>
                    Om du vill göra ändringar, klicka på ”Gå tillbaka och ändra”
                    Du kommer då till den del av formuläret där du kan justera
                    dina uppgifter.
                  </p>
                  <p>När du är klar klickar du på ”Skicka överklagan”.</p>

                  <div className="summary-section patient-summary mb-4">
                    <Heading as="h3" className="summary-section-heading">
                      Vad är det för nummer på tilläggsavgiften som ska
                      överklagas?
                    </Heading>

                    <div className="summary-data-wrapper mt-2 flex flex-col md:flex-row justify-between">
                      <div className="flex flex-col gap-2">
                        <span className="summary-data last">
                          {formData.surchargenumber}
                        </span>
                      </div>
                      <div className="flex flex-col">
                        <div className="summary-edit md:mb-6 mt-3 md:mt-0">
                          {GetEditButtonForNavigation(
                            "../tillaggsavgiftsnummer"
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <hr></hr>
                  <div className="summary-section patient-summary mb-4">
                    <Heading as="h3" className="summary-section-heading">
                      Var fanns biljetten?
                    </Heading>

                    <div className="summary-data-wrapper mt-2 flex flex-col md:flex-row justify-between">
                      <div className="flex flex-col gap-2">
                        <span className="summary-data last">
                          {getTicketsDetailsReadable(formData.ticketType)}
                        </span>
                      </div>
                      <div className="flex flex-col">
                        <div className="summary-edit md:mb-6 mt-3 md:mt-0">
                          {GetEditButtonForNavigation("../biljettdetaljer")}
                        </div>
                      </div>
                    </div>
                  </div>
                  <hr></hr>
                  {formData.ticketType === WITH_APP ? (
                    <>
                      <div className="summary-section patient-summary mb-4">
                        <Heading as="h3" className="summary-section-heading">
                          Appbiljett
                        </Heading>

                        <div className="summary-data-wrapper mt-2 flex flex-col md:flex-row justify-between">
                          <div>
                            <div>
                              <span className="summary-data-label">
                                Mobilnummer:
                              </span>
                              <span className="summary-data last">
                                {formData.ticketNumber}
                              </span>
                            </div>
                          </div>
                          <div className="flex flex-col">
                            <div className="summary-edit md:mb-6 mt-3 md:mt-0">
                              {GetEditButtonForNavigation("../biljett-med-app")}
                            </div>
                          </div>
                        </div>
                      </div>
                      <hr></hr>
                    </>
                  ) : formData.ticketType === WITH_TRAVELCARD ? (
                    <>
                      <div className="summary-section patient-summary mb-4">
                        <Heading as="h3" className="summary-section-heading">
                          Biljett via resekort
                        </Heading>

                        <div className="summary-data-wrapper mt-2 flex flex-col md:flex-row justify-between">
                          <div>
                            <div>
                              <span className="summary-data-label">
                                Resekortsnummer:
                              </span>
                              <span className="summary-data last">
                                {formData.ticketNumber}
                              </span>
                            </div>
                          </div>
                          <div className="flex flex-col">
                            <div className="summary-edit md:mb-6 mt-3 md:mt-0">
                              {GetEditButtonForNavigation(
                                "../biljett-med-resekort"
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      <hr></hr>
                    </>
                  ) : formData.ticketType === WITH_TICKET ? (
                    <>
                      <div className="summary-section patient-summary mb-4">
                        <Heading as="h3" className="summary-section-heading">
                          Biljett via Pappersbiljett
                        </Heading>

                        <div className="summary-data-wrapper mt-2 flex flex-col md:flex-row justify-between">
                          <div>
                            <div>
                              <span className="summary-data-label">
                                Biljett-ID:
                              </span>
                              <span className="summary-data last">
                                {formData.ticketNumber}
                              </span>
                            </div>
                          </div>
                          <div className="flex flex-col">
                            <div className="summary-edit md:mb-6 mt-3 md:mt-0">
                              {GetEditButtonForNavigation("../pappersbiljett")}
                            </div>
                          </div>
                        </div>
                      </div>
                      <hr></hr>
                    </>
                  ) : formData.ticketType === WITH_BLIP ? (
                    <>
                      <div className="summary-section patient-summary mb-4">
                        <Heading as="h3" className="summary-section-heading">
                          Biljett via bankkort
                        </Heading>

                        <div className="summary-data-wrapper mt-2 flex flex-col md:flex-row justify-between">
                          <div>
                            <div>
                              <span className="summary-data-label">
                                Biljett-ID:
                              </span>
                              <span className="summary-data last">
                                {formData.ticketNumber}
                              </span>
                            </div>
                          </div>
                          <div className="flex flex-col">
                            <div className="summary-edit md:mb-6 mt-3 md:mt-0">
                              {GetEditButtonForNavigation(
                                "../biljett-med-blipp"
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      <hr></hr>
                    </>
                  ) : null}

                  <div className="summary-section patient-summary mb-4">
                    <Heading as="h3" className="summary-section-heading">
                      Vad var det som hände?
                    </Heading>

                    <div className="summary-data-wrapper mt-2 flex flex-col md:flex-row justify-between">
                      <div className="flex flex-col gap-2">
                        <span className="summary-data last">
                          {formData.description}
                        </span>
                      </div>
                      <div className="flex flex-col">
                        <div className="summary-edit md:mb-6 mt-3 md:mt-0">
                          {GetEditButtonForNavigation(
                            "../vad-var-det-som-hande"
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <hr></hr>

                  <div className="summary-section patient-summary mb-4">
                    <Heading as="h3" className="summary-section-heading">
                      Är du över 18 år?
                    </Heading>

                    <div className="summary-data-wrapper mt-2 flex flex-col md:flex-row justify-between">
                      <div className="flex flex-col gap-2">
                        <span className="summary-data last">
                          {getAgeCheckReadable(formData.agecheck)}
                        </span>
                      </div>
                      <div className="flex flex-col">
                        <div className="summary-edit md:mb-6 mt-3 md:mt-0">
                          {GetEditButtonForNavigation("../ar-du-over-18")}
                        </div>
                      </div>
                    </div>
                  </div>
                  <hr></hr>
                  <div className="summary-section patient-summary mb-4">
                    <Heading as="h3" className="summary-section-heading">
                      Vem överklagar du för?
                    </Heading>

                    <div className="summary-data-wrapper mt-2 flex flex-col md:flex-row justify-between">
                      <div className="flex flex-col gap-2">
                        <span className="summary-data last">
                          {getAppealForWhoReadable(formData.appealforwho)}
                        </span>
                      </div>
                      <div className="flex flex-col">
                        <div className="summary-edit md:mb-6 mt-3 md:mt-0">
                          {GetEditButtonForNavigation(
                            "../vem-overklagar-du-for"
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <hr></hr>
                  <div className="summary-section patient-summary mb-4">
                    <Heading as="h3" className="summary-section-heading">
                      Kontaktuppgifter
                    </Heading>
                    <Heading as="h4">Resenär</Heading>

                    <div className="summary-data-wrapper ">
                      <div className="mt-2 flex flex-col md:flex-row justify-between">
                        <div className="flex flex-col gap-2">
                          <div>
                            <div>
                              {formData.agecheck === ADULT &&
                                formData.appealforwho === APPEAL_FOR_MYSELF ? (
                                <>
                                  <span className="summary-data-label">
                                    Förnamn:
                                  </span>
                                  <span className="summary-data">
                                    {formData.firstname}
                                  </span>
                                </>
                              ) : (
                                <>
                                  <span className="summary-data-label">
                                    Resenärens förnamn:
                                  </span>
                                  <span className="summary-data">
                                    {formData.firstname}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>

                          <div>
                            <div>
                              {formData.agecheck === ADULT &&
                                formData.appealforwho === APPEAL_FOR_MYSELF ? (
                                <>
                                  <span className="summary-data-label">
                                    Efternamn:
                                  </span>
                                  <span className="summary-data">
                                    {formData.lastname}
                                  </span>
                                </>
                              ) : (
                                <>
                                  <span className="summary-data-label">
                                    Resenärens efternamn:
                                  </span>
                                  <span className="summary-data">
                                    {formData.lastname}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>

                          <div>
                            <div>
                              {formData.agecheck === ADULT &&
                                formData.appealforwho === APPEAL_FOR_MYSELF ? (
                                <>
                                  <span className="summary-data-label">
                                    Telefonnummer:
                                  </span>
                                  <span className="summary-data">
                                    {formData.phone}
                                  </span>
                                </>
                              ) : (
                                <>
                                  <span className="summary-data-label">
                                    Resenärens telefonnummer:
                                  </span>
                                  <span className="summary-data">
                                    {formData.phone}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>

                          {formData.agecheck === ADULT &&
                            formData.appealforwho === APPEAL_FOR_MYSELF ? (
                            <>
                              <div>
                                <span className="summary-data-label">
                                  Mejladress:
                                </span>
                                <span className="summary-data">
                                  {formData.email}
                                </span>
                              </div>
                            </>
                          ) : (
                            <>
                              <Heading as="h4" className="mt-4">
                                Ombud/god man/vårdnadshavare
                              </Heading>
                              <div>
                                <span className="summary-data-label">
                                  Mejladress för kontakt:
                                </span>
                                <span className="summary-data">
                                  <span className="summary-data">
                                    {formData.reporterEmail}
                                  </span>
                                </span>
                              </div>
                            </>
                          )}
                        </div>

                        <div className="flex flex-col">
                          <div className="summary-edit md:mb-6 mt-3 md:mt-0">
                            {GetEditButtonForNavigation("../kontaktuppgifter")}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <hr></hr>
                  <div className="mt-6 mb-6 md:mb-8">
                    <fieldset className="border-0 p-0 m-0">
                      <legend className="sr-only">
                        Godkännande av personuppgifter
                      </legend>
                      <CheckboxForm width="fullWidth" name="gdprAccept">
                        Jag godkänner att Östgötatrafiken sparar och hanterar
                        mina personuppgifter enligt{" "}
                        <a
                          className="flex"
                          target="_blank"
                          rel="noreferrer"
                          href={URLS.privacyPolicy}
                        >
                          relevant dataskyddslagstiftning.
                          <Icon
                            className="inline-block ml-1"
                            name="external-link"
                            size={12}
                          />
                        </a>
                      </CheckboxForm>
                    </fieldset>
                  </div>
                </div>
              </Box>

              <div
                className={`flex justify-centery button-container ${isFormSubmitted === true ? "hide" : ""
                  }`}
              >
                <div className="w-full text-left flex items-center">
                  <Button
                    iconPosition="left"
                    iconSize={16}
                    iconName="arrow-left"
                    aria-label="Tillbaka"
                    showIcon
                    size="medium"
                    variant="outline"
                    onClick={(event) => {
                      event.preventDefault();
                      const backUrl = undefined;
                      if (backUrl) {
                        navigate(backUrl, { replace: true });
                      } else {
                        navigate(-1);
                      }
                    }}
                  >
                    Tillbaka
                  </Button>
                </div>
                <div className="w-full text-right">
                  <Button
                    type="submit"
                    disabled={isFormSubmitted}
                    onClick={handleSubmitClick(formRef)}
                  >
                    {isFormSubmitted ? (
                      <>
                        <Icon
                          name="loader-circle"
                          className="inline-block size-5 animate-spin"
                          aria-hidden="true"
                        />
                        Skickar...
                      </>
                    ) : (
                      "Skicka överklagan"
                    )}
                  </Button>
                </div>
              </div>
            </Form>
          </>
        )}
      </Formik>
    </div>
  );
}

function getTicketsDetailsReadable(ticketType: string): string {
  return ticketType === WITH_APP
    ? "I appen"
    : ticketType === WITH_TRAVELCARD
      ? "På ett resekort"
      : ticketType === WITH_BLIP
        ? "Blippat bankkort ombord"
        : ticketType === WITH_TICKET
          ? "Pappersbiljett"
          : ticketType === NO_TICKET
            ? "Inget av ovanstående/det fanns ingen biljett"
            : "";
}

function getAgeCheckReadable(agecheck: string): string {
  return agecheck === ADULT
    ? "Ja, jag är över 18 år"
    : agecheck === NOT_ADULT
      ? "Nej, jag är under 18 år"
      : "";
}

function getAppealForWhoReadable(appealforwho: string): string {
  return appealforwho === APPEAL_FOR_MYSELF
    ? "Jag överklagar för mig själv"
    : appealforwho === FIDUCIARY
      ? "Jag är god man/ombud/vårdnadshavare åt resenären"
      : appealforwho === PARENT
        ? "Jag är vårdnadshavare/förälder åt resenären"
        : appealforwho === HAVE_FIDUCIARY
          ? "Jag har en god man/ett ombud"
          : "Jag är under 18 år";
}

function formValidation(data: FormModel): any {
  return {
    surchargenumber: Yup.string().test(
      "valid-surchargenumber",
      "Du måste fylla i ett giltigt tilläggsavgiftsnummer.",
      () => data.surchargenumber.trim().length > 0
    ),

    firstname: Yup.string().test(
      "firstname-required",
      "Förnamn måste fyllas i.",
      () => data.firstname.trim().length > 0
    ),

    lastname: Yup.string().test(
      "lastname-required",
      "Efternamn måste fyllas i.",
      () => data.lastname.trim().length > 0
    ),

    phone: Yup.string().test(
      "phone-required",
      "Telefonnummer måste fyllas i.",
      () => data.phone.trim().length > 0
    ),
  };
}
